import { useScrollIntoView } from '@mantine/hooks';
import { useCallback, useState, type RefObject } from 'react';

export interface ValidationSummaryFeedback {
  animateValidationFeedback: boolean;
  clearValidationFeedback: () => void;
  revealValidationSummary: () => void;
  showValidationFeedback: boolean;
  validationSummaryRef: RefObject<HTMLDivElement | null>;
}

export const useValidationSummaryFeedback = (): ValidationSummaryFeedback => {
  const [showValidationFeedback, setShowValidationFeedback] = useState(false);
  const [animateValidationFeedback, setAnimateValidationFeedback] =
    useState(false);
  const { scrollableRef, scrollIntoView, targetRef } = useScrollIntoView<
    HTMLDivElement,
    HTMLElement
  >({
    duration: 200,
    offset: 16,
  });

  const clearValidationFeedback = useCallback(() => {
    setShowValidationFeedback(false);
    setAnimateValidationFeedback(false);
  }, []);

  const revealValidationSummary = useCallback(() => {
    setShowValidationFeedback(true);
    setAnimateValidationFeedback(false);

    const reveal = () => {
      const summary = targetRef.current;
      scrollableRef.current =
        summary?.closest<HTMLElement>('.mantine-Modal-content') ??
        summary?.closest<HTMLElement>('[role="dialog"]') ??
        null;
      scrollIntoView({ alignment: 'start' });
      summary?.focus({ preventScroll: true });
      setAnimateValidationFeedback(true);
    };

    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(reveal);
    } else {
      reveal();
    }
  }, [scrollableRef, scrollIntoView, targetRef]);

  return {
    animateValidationFeedback,
    clearValidationFeedback,
    revealValidationSummary,
    showValidationFeedback,
    validationSummaryRef: targetRef,
  };
};
